import { LocalNotifications } from '@capacitor/local-notifications';
import dayjs, { Dayjs } from 'dayjs';
import ActionTypes, { NotificationIds, PERIOD_REMINDER_IDS } from 'notifications';
import { getTrackingNotificationChannelId } from 'notifications/channels';
import posthog from 'posthog-js';
import { useTranslation } from 'react-i18next';
import Platform from 'utils/Platform';

export default function useSchedulePeriodNotifications() {
  const { t } = useTranslation();

  const cancelPeriodReminders = () => {
    LocalNotifications.getPending().then(pending => {
      const notificationsToCancel = pending.notifications.filter(n => PERIOD_REMINDER_IDS.includes(n.id));
      if (notificationsToCancel.length > 0) {
        LocalNotifications.cancel({
          notifications: notificationsToCancel,
        });
      }
    });
  };

  const schedulePeriodReminder = (nextPeriodLikelyStartDate: Dayjs, areDailyNotificationsEnabled: boolean) => {
    const channelId = getTrackingNotificationChannelId();
    const useEnhancedNotifications =
      Platform.getPlatform() === 'ios' && posthog.getFeatureFlag('ios-notifications-better-settings') === true;

    const buildNotification = (text: string, offsetToStart: number, notificationId: number) => {
      return {
        title: t('appName'),
        body: text,
        id: notificationId,
        schedule: {
          at: nextPeriodLikelyStartDate.add(offsetToStart, 'day').set('hour', 21).set('minute', 0).toDate(),
          allowWhileIdle: true,
        },
        actionTypeId: ActionTypes.PERIOD_REMINDER,
        ...(channelId ? { channelId } : {}),
        ...(useEnhancedNotifications ? { sound: 'default' } : {}),
      };
    };

    const notifications = [
      // period might start in 3 days
      buildNotification(
        t('symptomTracking.periodReminder.notifications.d_minus_3'),
        -3,
        NotificationIds.PERIOD_REMINDER_D_MINUS_3,
      ),
      // period might start tomorrow
      buildNotification(
        t('symptomTracking.periodReminder.notifications.d_minus_1'),
        -1,
        NotificationIds.PERIOD_REMINDER_D_MINUS_1,
      ),
      ...(areDailyNotificationsEnabled
        ? // we don't need post-period reminders since the user already has a daily reminder
          []
        : [
            // did your period start ?
            buildNotification(
              t('symptomTracking.periodReminder.notifications.d_plus_1'),
              1,
              NotificationIds.PERIOD_REMINDER_D_PLUS_1,
            ),
            // don't forget to track your period if it started
            buildNotification(
              t('symptomTracking.periodReminder.notifications.d_plus_4'),
              4,
              NotificationIds.PERIOD_REMINDER_D_PLUS_4,
            ),
            // i'll stop reminding you.
            buildNotification(
              t('symptomTracking.periodReminder.notifications.d_plus_10'),
              10,
              NotificationIds.PERIOD_REMINDER_D_PLUS_10,
            ),
          ]),
    ];

    const notificationsToSchedule = notifications.filter(notification => notification.schedule.at > dayjs().toDate());
    const notificationsToCancel = notifications.filter(notification => notification.schedule.at <= dayjs().toDate());

    if (notificationsToSchedule.length > 0) {
      LocalNotifications.schedule({
        notifications: notificationsToSchedule,
      });
    }

    if (notificationsToCancel.length > 0) {
      LocalNotifications.getPending().then(pending => {
        const existingNotifications = pending.notifications.filter(notification =>
          notificationsToCancel.map(toCancel => toCancel.id).includes(notification.id),
        );
        if (existingNotifications.length > 0) {
          LocalNotifications.cancel({
            notifications: existingNotifications,
          });
        }
      });
    }
  };

  return {
    schedulePeriodReminder,
    cancelPeriodReminders,
  };
}
